import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  ParamMap,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable } from 'rxjs';
import { AppStore } from '../store';
import { filter, take } from 'rxjs/operators';
import { CookieService, UrlService } from 'i2g-ng-utils';
import {
  NO_DISCOUNT_VALUE_COOKIE,
  PROMO_CODE,
  RETURN_URL_KEY,
} from '../../account/account.data';

const daysFromMinutes = (minutes) => (1 / 24 / 60) * minutes;

// NOTE: (Feb 7, 2021) This object is duplicated from
// 'billing/.../billing.data.ts' since Nx doesn't allow
// imports from other 'apps' only 'libs'. If you update
// this, update the exact same object in 'billing' too
const PARAMS = {
  ENCRYPTED_ACCOUNT_ID: 'eaid',
  PROMO_CODE: 'promocode',
  PROMO_CODE_CAMELCASE: 'promoCode',
  PROMO_CODE_SNAKECASE: 'promo_code', // this is the new naming convention of promo code
  PROMO: 'promo',
  EMAIL_SENT_DATE: 'dse',
  EMAIL_SENT_DATE_SNAKECASE: 'email_date', // this is the new naming convention of email sent date
  TAG: 'tag',
  TEST_GROUP: 'test_group',
  PLATFORM: 'platform',
  PLAN: 'plan',
  PAYWALL_VARIANT: 'paywall_variant',
};

const PARAM_PROMO_MAP = {
  [PARAMS.PROMO]: PARAMS.PROMO_CODE,
  [PARAMS.PAYWALL_VARIANT]: PARAMS.PAYWALL_VARIANT,
  [PARAMS.PLAN]: PARAMS.PLAN,
};

@Injectable()
export class AppGuard implements CanActivate {
  constructor(
    private _appStore: AppStore,
    private _cookieService: CookieService,
    private _urlService: UrlService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    Object.keys(PARAM_PROMO_MAP).forEach((key) => {
      this.saveUrlParamToCookie(route.queryParamMap, key, PARAM_PROMO_MAP[key]);
    });

    return this._appStore.isInitialized().pipe(
      filter((x) => !!x),
      take(1)
    );
  }

  saveUrlParamToCookie(
    urlParams: ParamMap,
    paramName: string,
    cookieName: string
  ): void {
    const returnUrl = urlParams.get(RETURN_URL_KEY);
    const returnUrlPromocode =
      returnUrl &&
      this._urlService.parseExternalUrl(returnUrl).query[paramName];
    const promocodeParam = returnUrlPromocode || urlParams.get(paramName);

    if (promocodeParam) {
      this._cookieService.setCookie(
        cookieName,
        promocodeParam,
        this._setCookieTime(cookieName, promocodeParam)
      );
    }
  }

  private _setCookieTime(
    cookieName: string,
    promocodeParam: string
  ): number | undefined {
    // 10 min TTL for cookie promocode: IMMEDIATESTART
    if (
      promocodeParam === NO_DISCOUNT_VALUE_COOKIE &&
      cookieName === PROMO_CODE
    ) {
      return daysFromMinutes(10);
    }

    // 30 min TTL for cookie paywall_variant
    if (cookieName === PARAMS.PAYWALL_VARIANT) {
      return daysFromMinutes(30);
    }

    // If cookie does not match these then return undefined to use cookie service default TTL
    return undefined;
  }
}
